/**
 * Every constant the flight core needs that is *not* a per-aircraft `.DAT`
 * field, in one register. AD-007e's rule applies: anything the sources do not
 * give is declared here as a named tunable with a stated provenance, never
 * buried as a literal in the physics.
 *
 * Provenance markers used below, matching the docs:
 * - **VERIFIED / REPORTED** — comes from `docs/engineering/flight-tunables.md`
 *   or the manual, with the section cited.
 * - **CALIBRATION-PENDING** — flight-tunables.md §8 says the data cannot supply
 *   it. The default is a physically reasonable starting point chosen so the
 *   *shape* of the behaviour is right; the number itself awaits the calibration
 *   workbench (AD-007e). Every such key is listed in
 *   `CALIBRATION_PENDING_TUNABLES`, and tests assert behaviour shape, not these
 *   values.
 * - **MEASURED-DOSBOX** — read off the original `RB.EXE` under DOSBox-X by the
 *   calibration lane (`re-sessions/calib/NOTES.md`, flight-tunables.md §8.1).
 *   Listed in `MEASURED_DOSBOX_TUNABLES`.
 *
 * Sibling registers are merged in rather than living here — the airframe's own
 * constants (mass, wing, lift curve, drag polar, sideslip) in
 * `aero-tunables.ts`, the guns' in `gunnery-tunables.ts`, the undercarriage's
 * in `gear-tunables.ts`, the AI pilots' in `ai-tunables.ts`, the bombs' in
 * `bomb-tunables.ts`, the departed regimes' (spiral and spin) in
 * `stall-tunables.ts`, and the man-and-weather ones (hypoxia, wounds,
 * carburettor ice) in `pilot-tunables.ts`. Downstream still sees exactly one
 * flat `Tunables` record and `makeTunables` still overrides any key by name;
 * the split is only so that "how an aeroplane flies" is one file's worth of
 * reading. What is left here is the powerplant, the control axes, the produced
 * turn and the elevator in `turn-tunables.ts`. Downstream still sees exactly one
 * flat `Tunables` record and `makeTunables` still overrides any key by name.
 */

import { AERO_TUNABLES } from "./aero-tunables.ts";
import { AI_TUNABLES, AI_TUNABLE_KEYS } from "./ai-tunables.ts";
import { BOMB_TUNABLES, BOMB_TUNABLE_KEYS } from "./bomb-tunables.ts";
import { GEAR_TUNABLES, GEAR_TUNABLE_KEYS } from "./gear-tunables.ts";
import { GUNNERY_TUNABLES, GUNNERY_TUNABLE_KEYS } from "./gunnery-tunables.ts";
import { PILOT_TUNABLES, PILOT_TUNABLE_KEYS } from "./pilot-tunables.ts";
import { STALL_TUNABLES, STALL_TUNABLE_KEYS } from "./stall-tunables.ts";
import { TURN_TUNABLES } from "./turn-tunables.ts";
import { WIND_TUNABLES, WIND_TUNABLE_KEYS } from "./wind-tunables.ts";

export const DEFAULT_TUNABLES = {
  ...AERO_TUNABLES,
  ...TURN_TUNABLES,

  /**
   * How much of the thrust vector points along the *flight path* rather than
   * along the nose: 1 = entirely along the path, 0 = entirely along the nose
   * (see `thrustDir` in forces.ts). MEASURED-DOSBOX at **1**, indirectly but
   * decisively. Nose-applied thrust puts a perpendicular component on the
   * velocity vector whose *angular* effect scales as `1/|v|`, so it blows up in
   * exactly the low-speed, high-α regime the calibration session flew: the
   * original's Camel zooms to ~20 mph and merely sags its nose
   * (re-sessions/calib/NOTES.md §4.5), and a 5-second banked full-back pull
   * swings 173° (flight-tunables.md §8.1) where nose thrust makes the same
   * commanded rate fly ~230°. On its own this is worth 8° of that 58°
   * overshoot; it is the *other* half of its value that settles the direction,
   * since applying thrust where `performance.ts` already assumed it acts takes
   * hands-off trimmed drift over 600 ticks from a fraction of a metre to
   * exactly zero, fleet-wide. §8.2 has the staged numbers.
   */
  thrustAlongPathFraction: 1,
  /** Shaft watts per unit of the `.DAT` power rating (offset 32). CALIBRATION-PENDING; 1900 W/unit puts the fleet in its real 80–230 hp band. */
  wattsPerPowerUnit: 1900,
  /** Propeller efficiency folded into thrust (AD-007c). CALIBRATION-PENDING. */
  propellerEfficiency: 0.75,
  /** Speed below which `ηP/V` is capped to a constant static thrust. CALIBRATION-PENDING. */
  staticThrustSpeedMps: 20,
  /** First-order throttle/engine spool lag. CALIBRATION-PENDING. */
  throttleLagSeconds: 0.6,
  /** Fallback power-lapse exponent when a spec's stored ceiling cannot be solved for (see aircraft-spec.ts). */
  defaultPowerLapseExponent: 1.0,
  /** Clamp on the solved per-aircraft power-lapse exponent, low end. */
  minPowerLapseExponent: 0.4,
  /** Clamp on the solved per-aircraft power-lapse exponent, high end. */
  maxPowerLapseExponent: 6,
  /** Specific fuel consumption. CALIBRATION-PENDING; 8.6e-8 kg/J ≈ 0.31 kg/kWh, which makes the manual's 2.5-hour endurance come out near real tank sizes. */
  fuelKgPerJoule: 8.6e-8,
  /** Manual, Realism Panel: "most of the aircraft had enough fuel to fly for two and a half hours". */
  fuelEnduranceSeconds: 9000,
  /** Power setting the endurance figure is quoted at. CALIBRATION-PENDING. */
  fuelCruisePowerFraction: 0.75,

  /** Max level speed fit slope, mph per (power/drag). VERIFIED — flight-tunables.md §1.1, r = 0.9775 over the 17 flyable scouts. */
  vmaxFitSlopeMph: 83.827,
  /** Max level speed fit intercept, mph. VERIFIED — flight-tunables.md §1.1. */
  vmaxFitInterceptMph: -3.45,

  /** Degrees per second per raw angular count, for the **turn fields (116/118)**. REPORTED (flight-tunables.md §2.1) and MEASURED-DOSBOX: the Camel's sustained turn came out 32.6/26.7 °/s against 31.0/23.3 predicted. Does *not* apply to the roll axis (§8.1), and — since AD-007j — no longer to the rudder field either; see `rudderDegPerSecondPerRateCount`. */
  degPerSecondPerRateCount: 1 / 30,
  /**
   * Degrees per second per raw count for the **rudder field (offset 54)**,
   * which AD-007j split off from the turn fields' unit.
   *
   * MEASURED-DOSBOX, and directly: experiment 1 held full right rudder on the
   * Camel for 18 s and the attitude yawed at a dead-steady **4.40 °/s**. The
   * Camel's field 54 is 180, so the unit is 180 ÷ 41, not the 180 ÷ 30 = 6.00
   * °/s the port used to derive. ÷ 30 is confirmed only for 116/118, where the
   * sustained-turn measurement backs it; nothing ever confirmed it here, and
   * §4.3's "22.4° at 6.37 s and still rising" — which AD-007i read as a
   * 6.00 °/s ceiling being fought down by a weathercock — is just 4.40 °/s with
   * nothing opposing it (4.40 × 6.37 = 28.0° raw, 22.3° once the keypress ramp
   * is allowed for).
   *
   * **Fleet check, ordered by AD-007j and recorded in flight-tunables.md
   * §8.3:** field 54 is 180 on 32 of the 33 aircraft and 600 on the Fokker
   * Dr.I alone, so the divisor is a single fleet-wide scale plus one exception.
   * ÷ 41 gives 4.39 °/s fleet-wide and 14.6 °/s for the Dr.I (against 6.00 and
   * 20.0 under ÷ 30). Both land the right side of plausible — 4.4 °/s is a slow
   * skid, which is what the trace shows, and the Dr.I's outlier field is the
   * one aircraft whose rudder was famously oversized. The divisor is MEASURED
   * on one aircraft; a Dr.I trace is what would confirm the field scales at all
   * rather than being a flag.
   */
  rudderDegPerSecondPerRateCount: 1 / 41,
  /** Control-response value treated as unity gain (the 400 class: D.H.2, Albatros D.III, Nieuport 17, S.E.5a, Pup). */
  controlResponseReference: 400,
  /** Roll-rate authority at unity control response. MEASURED-DOSBOX: 49.4 °/s on the Camel (field 42 = 600) and 37.3 °/s on the SPAD 13 (450) — proportional to offset 42 within 0.7 %, so 33.0 °/s at the 400 reference. */
  rollRateBaseDegPerSec: 33,
  /**
   * Pitch-rate authority at unity control response, before
   * `pitchRateResponseWeight` scales it by the field. MEASURED-DOSBOX: 22.1 °/s
   * on the Camel (offset 42 = 600) and 21.0 °/s on the SPAD 13 (450).
   */
  pitchRateBaseDegPerSec: 20.63,
  /**
   * How much of the pitch-rate ceiling scales with control response (offset
   * 42): `rate = base · (1 − w + w · resp/400)`. **1 would be the roll axis's
   * law and it is refuted** — the two measured aircraft are 5.2 % apart in
   * pitch rate across a 33 % spread in the field, which a proportional model
   * misses by ±13 %.
   *
   * What the data supports and what it cannot: a flat ceiling (`w = 0`) fits
   * both aircraft to 2.6 %, and 2.6 % is inside the measurement's own
   * precision — each rate is a difference of two stick-hold timings read off
   * the 18.2065 Hz BIOS tick over about a second, i.e. ±5 % — so **the two
   * points cannot discriminate a constant from a weak dependence, and no
   * fleet-wide shape is settled by them**. `w = 0.142` is the two-parameter fit
   * that reproduces both exactly; it is chosen over a bare constant only
   * because a constant extrapolates a flat pitch ceiling to the whole fleet
   * from two scouts 33 % apart in the one field, while this keeps the monotone
   * tie to the field the primary source calls "control response". The choice is
   * deliberately low-stakes: across the whole roster the two differ by under
   * 5 % (Morane 19.5 vs 20.6 °/s at the extreme), where the refuted
   * proportional model differed by 47 %.
   *
   * Two other one-parameter shapes were checked and rejected: `√resp` (4.9 %),
   * and proportionality to the stall/lift field 24, which fits best of all at
   * 1.2 % but would give a field with established semantics a second, unrelated
   * meaning on the strength of two points. A third aircraft settles it — the
   * Fokker Dr.I (offset 42 = 650) is predicted at 22.5 °/s here, 21.6 °/s under
   * a flat ceiling, and 26.8 °/s under the model this replaces.
   */
  pitchRateResponseWeight: 0.142,
  /**
   * True airspeed at and above which the controls command their full rate;
   * below it authority falls as the dynamic-pressure ratio `(V/Vfull)²` (see
   * `airspeedAuthority` in controls.ts). MEASURED-DOSBOX as a two-sided bound,
   * not a fitted value: every control rate the session timed was flown at
   * ~95 mph and must be reproduced exactly, the Camel sustains 1-g climbing
   * flight at **42.5 mph** (flight-tunables.md §8.1), and a full-back zoom
   * decaying past ~25 mph stops obeying the stick entirely — the nose sags
   * against full back pressure (re-sessions/calib/NOTES.md §4.5). 19 m/s is
   * exactly that 42.5 mph, i.e. the slowest speed the session saw controlled
   * flight at, so nothing measured is scaled down and everything below it is.
   */
  controlFullAuthoritySpeedMps: 19,
  /** First-order lag from commanded to actual body rate. CALIBRATION-PENDING. */
  rateLagSeconds: 0.12,
  /** Rudder authority retained under load at the fleet-standard value of field 26 (=120). REPORTED — flight-tunables.md §2.4. */
  rudderAuthorityReference: 120,

  /** Novice bank-to-turn assist: how much of the lift shortfall in a bank is made up automatically (1 = no nose-drop at all, per the manual). */
  noviceLiftAssistFraction: 1,
  /** Ceiling on the assist's lift multiplier so a knife-edge bank cannot conjure infinite lift. */
  noviceMaxLiftAssistFactor: 3,
  /** Novice auto-rudder gain against sideslip, rad/s per radian. CALIBRATION-PENDING. */
  noviceAutoRudderGain: 2.5,

  /**
   * Rolling-rate bias per unit of torque field 66 at full power.
   * MEASURED-DOSBOX: **zero**. Forty seconds hands-off in the original's Camel
   * at full throttle and Expert held bank at exactly 0.00°, and the model has no
   * auto-levelling to hide a bias. The rotary asymmetry lives in the turn-rate
   * pair at 116/118, not in a torque the pilot holds off.
   */
  rotaryTorqueRollGain: 0,
  /**
   * AD-007m: gyroscopic precession, the pitch→yaw half only — body yaw rate per
   * unit of body pitch rate on the Camel, whose field 68 is
   * `rotaryPrecessionReferenceTorque`.
   *
   * MEASURED-DOSBOX, four intervals and both stick directions
   * (flight-tunables.md §8.5.4): full back gives r/q = +0.35 and +0.36, full
   * forward +0.39 and +0.36, and averaging the two directions separates a
   * constant part of −0.37 °/s ≈ 0 from a rate-proportional part of
   * **r = 0.36·q**. A cross-axis term proportional to the applied rate and
   * *reversing* with it is precession's signature, and the S.E.5a — a
   * stationary V-8 with `rotCouple = 0` and `torque = [0,0]` — shows none of it,
   * which is what gates the term to the rotary-torque airframes.
   *
   * This replaces `rotaryGyroGain`, a single constant that drove *both*
   * coupling directions. That could not survive §8.5.4: rudder→pitch is
   * measured at exactly 0.00° twice on two aircraft while pitch→yaw is 0.36 on
   * the Camel, and one constant cannot be both. The rudder→pitch direction is
   * therefore not parameterised at all any more — it is a hard structural zero,
   * on the same rule that deleted `sideforcePerRad` and
   * `weathercockYawRatePerRad`.
   */
  rotaryPrecessionYawPerPitchRate: 0.36,
  /**
   * The value of torque field 68 that `rotaryPrecessionYawPerPitchRate` was
   * measured at — the Camel's 200, the fleet's largest — so that the gain
   * scales across the other seven rotary-torque airframes as the *ratio* of
   * their field 68 to this one (Dr.I, Snipe, Siemens and the Triplane at 100
   * get 0.18; the Morane/Fokker E.III/Nieuport 17 group at 50 gets 0.09).
   *
   * CALIBRATION-PENDING, and it is the *shape* that is pending, not the
   * reference: one aeroplane has ever been measured, so "scales with the torque
   * field" and "constant across all rotaries" fit the single data point equally
   * well. Torque-proportional is the physical reading (precession is
   * proportional to rotor angular momentum, which is what the field is read as)
   * and it is the conservative one for the fleet, since it makes the milder
   * rotaries milder rather than giving them the Camel's coupling wholesale. One
   * more rotary type flown settles it — the Fokker Dr.I is queued (§8.5.6), and
   * its field 68 of 100 predicts r = 0.18·q here against 0.36·q if the gain is
   * constant, which is a 2× separation a single trace can resolve.
   */
  rotaryPrecessionReferenceTorque: 200,
  /** Slipstream/P-factor yaw bias per unit of field 56 at full power. MEASURED-DOSBOX: **zero** — heading held to the last stored digit over the same 40 s hands-off run. Field 56's meaning stays unknown; it is not a steady yaw bias. */
  rotarySlipstreamGain: 0,

  /** Limit load factor per unit of wing strength (offset 104). CALIBRATION-PENDING; ÷500 puts the fleet at 4.4 g (D.VIII) to 6.6 g. */
  wingStrengthPerG: 500,
  /** Negative-g limit as a fraction of the positive limit — the manual's Albatros "upper wings shear at high negative G" behaviour, applied fleet-wide since the data has no negative-g field. CALIBRATION-PENDING. */
  negativeGLimitFactor: 0.55,
  /** Extra stress per unit of overspeed beyond Vmax — the manual's failure mode is specifically a *high-speed* dive. CALIBRATION-PENDING. */
  diveOverspeedStressGain: 0.6,
  /** Wing-failure hazard per second at twice the limit load. CALIBRATION-PENDING. */
  overstressFailureRatePerSecond: 3,
  /** Lift retained after a wing fails. CALIBRATION-PENDING. */
  wingFailureLiftFactor: 0.35,
  /** Uncommanded roll after a wing fails. CALIBRATION-PENDING. */
  wingFailureRollRadPerSec: 1.5,
  /** How hard a wrecked airframe weathervanes towards zero alpha, per radian of alpha (AD-014). CALIBRATION-PENDING. */
  wingFailurePitchGain: 2,
  /** Ceiling on that weathervane rate, rad/s, so a wreck noses over rather than snapping over (AD-014). CALIBRATION-PENDING. */
  wingFailurePitchRadPerSec: 0.6,
  /** Worst-case control authority left when the control surfaces are fully damaged. CALIBRATION-PENDING. */
  controlDamageFloor: 0.25,

  /** Descent rate at ground contact above which the arrival counts as a crash. CALIBRATION-PENDING. */
  crashDescentRateMps: 6,

  /** Forward stick a dead pilot slumps onto, which is what noses the wreck down (AD-014). CALIBRATION-PENDING. */
  deadPilotStickPitch: -0.35,
  /** Aileron a dead pilot slumps across, which is what turns the descent into a spiral (AD-014). CALIBRATION-PENDING. */
  deadPilotStickRoll: 0.25,
  /**
   * The bank a wreck's slumped stick rolls *to* and then holds, radians
   * (AD-014b, `derelict.ts`'s `wreckStickRoll`). Held rather than exceeded
   * because a constant aileron deflection is a barrel roll, and a barrel roll
   * carries the nose-down bias over the top once a revolution and throws the
   * dive away — the roll-axis twin of the loop AD-014 already forbade on the
   * pitch axis. 75° is the graveyard spiral: steep enough that the lift vector
   * has almost no vertical component left to hold the wreck up, short of the
   * knife-edge where it would start climbing again. CALIBRATION-PENDING.
   */
  wreckSpiralBankRad: 1.309,
  /**
   * How long a wreck may keep falling before the encounter writes it off where
   * it is (AD-014). A fall from height genuinely takes this long and the player
   * is free to fly during it; the cap exists so a wreck that finds a thermal —
   * or a mission flown above 4000 m — cannot hold the verdict open forever.
   * CALIBRATION-PENDING. 45 s covers a wreck falling from about 2200 m, which is
   * above where dogfights are actually fought (a kill at the /fly spawn height
   * of 1200–1500 m arrives in ~31 s), so in the ordinary case the timeout never
   * fires and the player watches the whole thing.
   */
  wreckFallTimeoutSeconds: 60,

  ...STALL_TUNABLES,
  ...PILOT_TUNABLES,
  ...GEAR_TUNABLES,
  ...GUNNERY_TUNABLES,
  ...AI_TUNABLES,
  ...BOMB_TUNABLES,
  ...WIND_TUNABLES,
} satisfies Record<string, number>;

export type Tunables = Readonly<typeof DEFAULT_TUNABLES>;
export type TunableKey = keyof Tunables;

export const TUNABLE_KEYS: readonly TunableKey[] = Object.keys(DEFAULT_TUNABLES).filter(
  (key): key is TunableKey => key in DEFAULT_TUNABLES,
);

/** Keys measured against the original executable under DOSBox-X, so no longer pending. Provenance: `re-sessions/calib/NOTES.md`; summary: `docs/engineering/flight-tunables.md` §8.1. */
export const MEASURED_DOSBOX_TUNABLES: readonly TunableKey[] = [
  "clMaxPerStallUnit",
  "thrustAlongPathFraction",
  "controlFullAuthoritySpeedMps",
  "rollRateBaseDegPerSec",
  "pitchRateBaseDegPerSec",
  "pitchRateResponseWeight",
  "rotaryTorqueRollGain",
  "rotarySlipstreamGain",
  "rotaryPrecessionYawPerPitchRate",
  "turnRateCapGain",
  "turnRateBankSaturationDeg",
  "turnRateBankShare",
  "turnRatePullGain",
  "rudderDegPerSecondPerRateCount",
  "pathAlignRatePerRad",
  "slipDragPerRad2",
  "elevatorLoadFactorMax",
];

/**
 * The keys flight-tunables.md §8 marks as unsourceable from the game data.
 * Kept as an explicit list so the calibration lane has a work queue and so a
 * test can assert the register has not silently drifted from the defaults.
 */
export const CALIBRATION_PENDING_TUNABLES: readonly TunableKey[] = [
  "massPerDragUnitKg",
  "wingLoadingKgPerM2",
  "liftCurveSlopePerRad",
  "postStallClFactor",
  "postStallSpreadRad",
  "stallDragPerRad",
  "inducedDragFactor",
  "alphaRelaxRatePerRad",
  "alphaRelaxLowSpeedFloor",
  "alphaFollowStallFraction",
  // Measured at 5.5° but shipped at 0, so the *value* is calibration work
  // rather than settled fact — the queue entry is "turn it on and re-derive the
  // four attitude rails it moves". See `wingIncidenceRad` in aero-tunables.ts.
  "wingIncidenceRad",
  "elevatorLoadFactorMin",
  "elevatorPullDragGain",
  "rollRightPenalty",
  "rotaryPrecessionReferenceTorque",
  "wattsPerPowerUnit",
  "propellerEfficiency",
  "staticThrustSpeedMps",
  "throttleLagSeconds",
  "fuelKgPerJoule",
  "fuelCruisePowerFraction",
  "rateLagSeconds",
  "noviceAutoRudderGain",
  "turnInducedDragGain",
  "wingStrengthPerG",
  "negativeGLimitFactor",
  "diveOverspeedStressGain",
  "overstressFailureRatePerSecond",
  "wingFailureLiftFactor",
  "wingFailureRollRadPerSec",
  "wingFailurePitchGain",
  "wingFailurePitchRadPerSec",
  "controlDamageFloor",
  "crashDescentRateMps",
  "deadPilotStickPitch",
  "deadPilotStickRoll",
  "wreckSpiralBankRad",
  "wreckFallTimeoutSeconds",
  ...STALL_TUNABLE_KEYS,
  ...PILOT_TUNABLE_KEYS,
  ...GEAR_TUNABLE_KEYS,
  ...GUNNERY_TUNABLE_KEYS,
  ...AI_TUNABLE_KEYS,
  ...BOMB_TUNABLE_KEYS,
  ...WIND_TUNABLE_KEYS,
];

/** Builds a full tunable set from the defaults plus any overrides (tests and the calibration workbench pass partial sets). */
export function makeTunables(overrides: Partial<Tunables> = {}): Tunables {
  return { ...DEFAULT_TUNABLES, ...overrides };
}
